import moment from "moment";
import {
  calculateDuration,
  convertTimeToDatetimeLocal,
  formatAmount,
  isCheckNextDayShift,
  parseBooleanVal,
  rosterInvoiceLineTotal,
  truncateDecimal,
} from "Utils/utils";
import {
  getBillingShiftDateTimes,
  getShiftDurationMinutes,
} from "./billingShiftRange";

const parseSelectedRatioDivisor = (ratio) => {
  if (!ratio) return null;
  const parts = String(ratio).split(":");
  const n = parseInt(parts[1], 10);
  return !Number.isNaN(n) && n > 0 ? n : null;
};

const isAdvanceMultiAssignmentShift = (shift) =>
  shift?.is_v5_multi_assignment === true || shift?.is_multi_assignment === true;

const parseRate = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

const resolveParticipantCount = (shiftDetails) =>
  Math.max(1, shiftDetails?.participants?.length || 1);

/** Active-hours only: advance sleepover uses the selected ratio, nothing else. */
const resolveActiveHoursParticipantCount = ({
  shiftDetails,
  watchFormData,
  index,
  fallback,
}) => {
  if (!isAdvanceMultiAssignmentShift(shiftDetails)) {
    return fallback;
  }
  return (
    parseSelectedRatioDivisor(watchFormData?.[`ratio_${index}`]) ?? fallback
  );
};

const resolveCatalogueRate = (ndisRateField, rateAmountField, fallbackRate) => {
  if (ndisRateField === "alternate") {
    return parseRate(rateAmountField);
  }
  return parseRate(ndisRateField ?? rateAmountField ?? fallbackRate);
};

const resolveBreakdownSegmentDuration = ({
  index,
  item,
  watchFormData,
  startTime,
  baseDate,
  useNextDayFromPrevious,
}) => {
  const formStart = watchFormData?.[`start_time_${index}`];
  const formEnd = watchFormData?.[`end_time_${index}`];

  if (formStart && formEnd && startTime && baseDate) {
    const isNextDay =
      isCheckNextDayShift(startTime, formStart, formEnd) !== false;
    let segmentBaseDate = baseDate;

    if (useNextDayFromPrevious) {
      segmentBaseDate = moment(segmentBaseDate)
        .add(1, "days")
        .format("YYYY-MM-DD");
    }

    const segmentStart = convertTimeToDatetimeLocal(segmentBaseDate, formStart);
    const segmentEnd = isNextDay
      ? isCheckNextDayShift(segmentBaseDate, formStart, formEnd)
      : convertTimeToDatetimeLocal(segmentBaseDate, formEnd);

    return {
      duration: calculateDuration(segmentStart, segmentEnd),
      segmentStart,
      segmentEnd,
      isNextDay,
    };
  }

  if (item?.start_time && item?.end_time) {
    return {
      duration: calculateDuration(item.start_time, item.end_time),
      segmentStart: item.start_time,
      segmentEnd: item.end_time,
      isNextDay: false,
    };
  }

  return {
    duration: item?.duration || null,
    segmentStart: item?.start_time ?? null,
    segmentEnd: item?.end_time ?? null,
    isNextDay: false,
  };
};

const addBreakdownLineTotal = ({
  sum,
  duration,
  rate,
  participants,
  sleepover,
  isSta = false,
  startTime,
  endTime,
}) => {
  if (rate == null || rate <= 0 || !duration) return sum;
  return (
    sum +
    rosterInvoiceLineTotal(duration, rate, participants, {
      sleepover,
      isSta,
      parentId: null,
      startTime,
      endTime,
    })
  );
};

const calculateBreakdownInvoiceTotal = ({
  breakdownData,
  watchFormData,
  shiftDetails,
  startTime,
}) => {
  if (!Array.isArray(breakdownData) || breakdownData.length === 0) return 0;

  const shiftIsSta =
    parseBooleanVal(shiftDetails?.is_sta ?? shiftDetails?.sta) ||
    shiftDetails?.is_sta === 1 ||
    String(shiftDetails?.is_sta ?? shiftDetails?.sta) === "1";
  let sum = 0;
  let useNextDay = false;
  const baseDate = startTime ? moment(startTime).format("YYYY-MM-DD") : null;

  breakdownData.forEach((item, index) => {
    const {
      duration,
      segmentStart,
      segmentEnd,
      isNextDay,
    } = resolveBreakdownSegmentDuration({
      index,
      item,
      watchFormData,
      startTime,
      baseDate,
      useNextDayFromPrevious: useNextDay,
    });

    useNextDay = isNextDay;

    const invoiceNdisRate = watchFormData?.[`invoice_ndis_rate_${index}`];
    const rate = resolveCatalogueRate(
      invoiceNdisRate,
      watchFormData?.[`rate_amount_${index}`],
      item?.invoice_price ?? item?.ndis_rate
    );

    const sleepover = shiftDetails?.is_split_shift
      ? Boolean(item?.sleepover ?? item?.is_sleepover)
      : Boolean(
          watchFormData?.sleepover && watchFormData?.[`sleepover_${index}`]
        );
    const isSta =
      parseBooleanVal(item?.is_sta) ||
      item?.is_sta === 1 ||
      String(item?.is_sta) === "1" ||
      shiftIsSta;

    const participants = resolveParticipantCount(shiftDetails);

    sum = addBreakdownLineTotal({
      sum,
      duration,
      rate,
      participants,
      sleepover,
      isSta,
      startTime: segmentStart,
      endTime: segmentEnd,
    });

    const activeHoursDuration = watchFormData?.[`active_hours_${index}`];
    const activeHoursItemId =
      watchFormData?.[`invoice_item_id_active_hours${index}`];
    const activeHoursNdisRate =
      watchFormData?.[`invoice_ndis_rate_active_hours${index}`];

    if (
      sleepover &&
      activeHoursItemId &&
      activeHoursDuration !== "" &&
      activeHoursDuration !== undefined &&
      activeHoursDuration != null
    ) {
      const activeHoursRate = resolveCatalogueRate(
        activeHoursNdisRate,
        watchFormData?.[`rate_amount_active_hours${index}`],
        item?.children?.[0]?.invoice_price
      );

      sum = addBreakdownLineTotal({
        sum,
        duration: activeHoursDuration,
        rate: activeHoursRate,
        participants: resolveActiveHoursParticipantCount({
          shiftDetails,
          watchFormData,
          index,
          fallback: participants,
        }),
        sleepover: false,
        isSta: false,
      });
    }
  });

  return sum;
};

const calculateSingleLineInvoiceTotal = ({
  watchFormData,
  shiftDetails,
  timeDisplayPreference,
  ndisItemName,
}) => {
  const participants = resolveParticipantCount(shiftDetails);
  const rate = resolveCatalogueRate(
    watchFormData?.invoice_ndis_rate,
    watchFormData?.rate_amount,
    ndisItemName?.price ?? shiftDetails?.invoice_price
  );

  if (rate == null || rate <= 0) return 0;

  const { start, end } = getBillingShiftDateTimes(
    shiftDetails,
    timeDisplayPreference
  );
  const startTime = start?.format("YYYY-MM-DD HH:mm:ss") ?? null;
  const endTime = end?.format("YYYY-MM-DD HH:mm:ss") ?? null;

  let duration = null;
  if (startTime && endTime) {
    duration = calculateDuration(startTime, endTime);
  } else {
    const durationMinutes = getShiftDurationMinutes(
      shiftDetails,
      timeDisplayPreference
    );
    if (durationMinutes != null) {
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      duration = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}:00`;
    }
  }

  return addBreakdownLineTotal({
    sum: 0,
    duration,
    rate,
    participants,
    sleepover: Boolean(watchFormData?.sleepover),
    isSta:
      parseBooleanVal(shiftDetails?.is_sta ?? shiftDetails?.sta) ||
      shiftDetails?.is_sta === 1 ||
      String(shiftDetails?.is_sta ?? shiftDetails?.sta) === "1",
    startTime,
    endTime,
  });
};

const calculateKmInvoiceTotal = ({
  watchFormData,
  shiftDetails,
  travelNdisNum,
  ndisitemRate,
}) => {
  if (!parseBooleanVal(watchFormData?.applykm)) return 0;

  const km = parseFloat(watchFormData?.km);
  if (!Number.isFinite(km) || km <= 0) return 0;

  const useAlternateRate =
    ndisitemRate?.value === "alternate" ||
    parseBooleanVal(travelNdisNum?.km_alternative_rate);

  const unitPrice = useAlternateRate
    ? parseRate(
        watchFormData?.km_alternate_nids_rate_amount ??
          travelNdisNum?.km_alternative_price
      )
    : parseRate(travelNdisNum?.price ?? ndisitemRate?.value);

  if (unitPrice == null || unitPrice <= 0) return 0;

  const participants = resolveParticipantCount(shiftDetails);
  return truncateDecimal((km * unitPrice) / participants);
};

/**
 * Live invoice estimate for Code Allocation — mirrors roster invoicing totals:
 * service line(s) + optional KM travel line, with alternate rates and ratio split.
 */
export const calculateCodeAllocationInvoiceEstimate = ({
  watchFormData,
  shiftDetails,
  timeDisplayPreference,
  timesheetBreakdown,
  travelNdisNum,
  ndisitemRate,
  ndisItemName,
  startTime,
}) => {
  if (!shiftDetails || parseBooleanVal(watchFormData?.non_billable)) {
    return 0;
  }

  let total = 0;
  const useBreakdown =
    Boolean(watchFormData?.invoice_breakdown) ||
    (shiftDetails?.is_split_shift &&
      Array.isArray(shiftDetails?.invoiceTimesheets) &&
      shiftDetails.invoiceTimesheets.length > 0);

  if (useBreakdown) {
    const breakdownData =
      timesheetBreakdown?.length > 0
        ? timesheetBreakdown
        : shiftDetails?.invoiceTimesheets;

    total += calculateBreakdownInvoiceTotal({
      breakdownData,
      watchFormData,
      shiftDetails,
      startTime,
    });
  } else {
    total += calculateSingleLineInvoiceTotal({
      watchFormData,
      shiftDetails,
      timeDisplayPreference,
      ndisItemName,
    });
  }

  total += calculateKmInvoiceTotal({
    watchFormData,
    shiftDetails,
    travelNdisNum,
    ndisitemRate,
  });

  return truncateDecimal(total);
};

export const formatCodeAllocationInvoiceEstimate = (amount) => {
  if (amount == null || Number.isNaN(amount)) return null;
  return `$${formatAmount(amount)}`;
};
